import { FC, ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import type { Channel as StreamChannel } from 'stream-chat'

import { LoadingSpinner, ZeroState } from '@/components'
import { MessageDetailContainer } from '@/containers'
import { useChatConnection } from '@/context/ChatConnectionContext'
import { trans } from '@/helpers/trans'

interface QueryResult {
    channelId: string
    channel?: StreamChannel
}

export const ConnectedMessageDetail: FC = (): ReactElement => {
    const { channelId = '' } = useParams()
    const { client, hasConnectionError, setOpenChannelCid } = useChatConnection()
    const [result, setResult] = useState<QueryResult>()
    const openChannelCid = result?.channel?.cid

    useEffect(() => {
        if (! client) {
            return undefined
        }

        let cancelled = false

        client.queryChannels(
            { id: { $eq: channelId }, members: { $in: [client.userID ?? ''] } },
            {},
            { state: true, watch: true },
        ).then(([foundChannel]) => {
            if (! cancelled) {
                setResult({ channelId, channel: foundChannel })
            }
        }).catch(error => {
            console.error(error)

            if (! cancelled) {
                setResult({ channelId, channel: undefined })
            }
        })

        return () => {
            cancelled = true
        }
    }, [client, channelId])

    useEffect(() => {
        setOpenChannelCid(openChannelCid)

        return () => setOpenChannelCid(undefined)
    }, [openChannelCid, setOpenChannelCid])

    if (hasConnectionError) {
        return <ZeroState text={trans('pages.messages.connectionError')} />
    }

    const isLoading = ! client || result?.channelId !== channelId

    if (isLoading) {
        return <LoadingSpinner />
    }

    const { channel } = result

    return (
        <MessageDetailContainer
            channel={channel}
            client={client}
        />
    )
}
